import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import s from "./LabTrendChart.module.css";

/* A measured value on a date, for one analyte. `value` is displayed and
   plotted as measured — nothing here scales, indexes, smooths or normalises
   it, and there is deliberately no reference range in this component's props
   at all. See "The software does not interpret" in the root CLAUDE.md and
   REQ-MDR-002: charting values over time is permitted display-only, and what
   that permission rests on is that the chart draws numbers on a scale and
   does nothing else with them. */
export interface LabTrendPoint {
  /** "YYYY-MM-DD", the draw's collection date. */
  collectedOn: string;
  value: number;
  /** Provenance, not quality: a value the client typed in from a report
      outside Ferrivia is drawn as an open marker so a reader can see which
      points a lab actually ran. */
  selfReported: boolean;
}

export interface LabTrendSeries {
  analyteCode: string;
  label: string;
  unit: string;
  /** Which of the three chart-series tokens paints this line. Series colour
      identifies *which analyte* and nothing else — no value is ever coloured
      by where it falls. */
  seriesIndex: 1 | 2 | 3;
  /** Ordered oldest-first. A draw that carried no measurement for this
      analyte is simply absent, which breaks the line rather than
      interpolating a number nobody measured. */
  points: LabTrendPoint[];
  /** The y-axis this series is drawn against, with the ticks to print.
      Derived from the data by `trendAxis`, never from a reference range. */
  axis: TrendAxis;
}

export interface TrendAxis {
  min: number;
  max: number;
  ticks: number[];
}

export interface LabTrendChartProps {
  series: LabTrendSeries[];
  /** Every distinct draw date across all series, oldest first — the shared
      time axis, and the columns hover snaps to. */
  dates: string[];
  /** Formats a date for the axis ends and the tooltip heading. */
  formatDate: (collectedOn: string) => string;
}

const VIEW_W = 288;
const PAD_L = 34;
const PAD_R = 10;
const PANEL_H = 68;
const LAST_PANEL_H = 84;
const PAD_T = 20;
const PAD_B = 6;
const LAST_PAD_B = 20;

/* Exported for the combined chart next door, which plots the same points on
   the same calendar-proportional time axis. */
export function dayMs(collectedOn: string): number {
  return new Date(`${collectedOn}T00:00:00Z`).getTime();
}

/* A "nice" axis covering the values actually measured, with three printed
   ticks. Zero-based where the data allows it, and otherwise stepped down to a
   round number below the lowest reading — a haemoglobin series zeroed to 0
   flattens to a straight line and shows nothing.

   Two properties matter and both are about honesty rather than looks: the
   domain comes only from this analyte's own measurements (never from a
   reference range, which would make the axis an implicit comparison), and
   every tick it produces is printed on the panel, so the scale a line was
   drawn on is always visible rather than implied. */
export function trendAxis(values: number[]): TrendAxis {
  if (values.length === 0) {
    return { min: 0, max: 1, ticks: [0, 1] };
  }
  const lowest = Math.min(...values);
  const highest = Math.max(...values);
  const span = highest - lowest || Math.abs(highest) || 1;
  const step = niceStep(span / 2);

  // Zero-based unless the data sits far enough above zero that a zeroed axis
  // would waste the whole panel — the threshold is the data's own spread, not
  // a fixed number, so a tight series near zero still gets a zero baseline.
  // CRP around 1–2 mg/L keeps its zero; haemoglobin around 120 g/L, whose
  // spread is a tenth of its distance from zero, would flatten to a straight
  // line and gets a stepped baseline instead — printed, never implied.
  const min = lowest <= span * 2 ? 0 : Math.floor((lowest - span / 2) / step) * step;
  const max = Math.ceil((highest + span / 4) / step) * step;
  const mid = round(min + (max - min) / 2);
  return { min, max, ticks: [min, mid, max].map(round) };
}

function niceStep(rough: number): number {
  if (rough <= 0) {
    return 1;
  }
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const stepped = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return stepped * magnitude;
}

// Trims float noise from the arithmetic above (0.30000000000000004) without
// rounding a real measurement — this only ever runs on axis bounds.
function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/* One figure, three stacked panels, one shared time axis along the bottom.

   Not one overlaid y-axis: ferritin, CRP and haemoglobin are measured on
   scales two orders of magnitude apart, so on a single axis CRP collapses
   onto the baseline and haemoglobin pins to the ceiling — two of the three
   series become unreadable. Each panel keeps its own scale and prints its own
   ticks, and the shared x lets a reader line up the same draw across all
   three. */
export function LabTrendChart({ series, dates, formatDate }: LabTrendChartProps) {
  const { t } = useTranslation("labs");
  const frameRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [tooltipLeft, setTooltipLeft] = useState(0);

  const domain = useMemo(() => {
    const stamps = dates.map(dayMs);
    return { min: Math.min(...stamps), max: Math.max(...stamps) };
  }, [dates]);

  const x = useMemo(() => {
    const plotW = VIEW_W - PAD_L - PAD_R;
    const xSpan = domain.max - domain.min || 1;
    return (collectedOn: string) => PAD_L + ((dayMs(collectedOn) - domain.min) / xSpan) * plotW;
  }, [domain]);

  const active = activeDate;

  // Positioned in measured pixels rather than a percentage of VIEW_W: a
  // percentage clamp can't know how wide the tooltip's own text renders, so
  // in a narrow pane (this chart lives in a 320-400px sidebar) a long row
  // like "Hemoglobiini 134 g/L" pushed past the frame's edge and got clipped
  // by the scrolling pane around it rather than actually reflowing.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const tooltip = tooltipRef.current;
    if (active === null || !frame || !tooltip) {
      return;
    }
    const frameWidth = frame.getBoundingClientRect().width;
    const half = tooltip.getBoundingClientRect().width / 2;
    const ideal = (x(active) / VIEW_W) * frameWidth;
    setTooltipLeft(Math.min(frameWidth - half, Math.max(half, ideal)));
  }, [active, x]);

  if (dates.length < 2 || series.length === 0) {
    return null;
  }

  function nearestDate(clientX: number): string | null {
    const frame = frameRef.current;
    if (!frame) {
      return null;
    }
    const rect = frame.getBoundingClientRect();
    const local = ((clientX - rect.left) / rect.width) * VIEW_W;
    let best: string | null = null;
    let bestDistance = Infinity;
    for (const date of dates) {
      const distance = Math.abs(x(date) - local);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = date;
      }
    }
    return best;
  }

  return (
    <div className={s.root}>
      <div
        ref={frameRef}
        className={s.frame}
        onMouseMove={(e) => setActiveDate(nearestDate(e.clientX))}
        onMouseLeave={() => setActiveDate(null)}
        onTouchStart={(e) => setActiveDate(nearestDate(e.touches[0]!.clientX))}
        onTouchMove={(e) => setActiveDate(nearestDate(e.touches[0]!.clientX))}
        onTouchEnd={() => setActiveDate(null)}
      >
        {series.map((entry, index) => {
          const isLast = index === series.length - 1;
          const height = isLast ? LAST_PANEL_H : PANEL_H;
          const padB = isLast ? LAST_PAD_B : PAD_B;
          const plotH = height - PAD_T - padB;
          const axis = entry.axis;
          const y = (value: number) =>
            PAD_T + plotH - ((value - axis.min) / (axis.max - axis.min || 1)) * plotH;

          return (
            <svg
              key={entry.analyteCode}
              className={s.panel}
              viewBox={`0 0 ${VIEW_W} ${height}`}
              role="img"
              aria-label={t("trend.panelLabel", "{{analyte}} ({{unit}}), {{count}} mittausta", {
                analyte: entry.label,
                unit: entry.unit,
                count: entry.points.length,
              })}
            >
              {axis.ticks.map((tick) => (
                <g key={tick}>
                  <line
                    x1={PAD_L}
                    y1={y(tick)}
                    x2={VIEW_W - PAD_R}
                    y2={y(tick)}
                    className={s.grid}
                  />
                  <text x={PAD_L - 6} y={y(tick)} className={s.tick} textAnchor="end" dominantBaseline="middle">
                    {tick}
                  </text>
                </g>
              ))}

              {/* The panel title doubles as the series' direct label, so which
                  line is which never depends on colour alone. */}
              <text x={PAD_L} y={PAD_T - 7} className={s.panelTitle} data-series={entry.seriesIndex}>
                {entry.label} ({entry.unit})
              </text>

              {segments(entry.points, dates).map((segment, i) => (
                <polyline
                  key={i}
                  className={s.line}
                  data-series={entry.seriesIndex}
                  points={segment.map((p) => `${x(p.collectedOn)},${y(p.value)}`).join(" ")}
                />
              ))}

              {entry.points.map((point) => (
                <circle
                  key={point.collectedOn}
                  cx={x(point.collectedOn)}
                  cy={y(point.value)}
                  r={3.4}
                  className={point.selfReported ? s.markerOpen : s.marker}
                  data-series={entry.seriesIndex}
                />
              ))}

              {active !== null && (
                <line x1={x(active)} y1={4} x2={x(active)} y2={height - 2} className={s.crosshair} />
              )}

              {isLast && (
                <>
                  <text x={PAD_L} y={height - 3} className={s.axisDate} textAnchor="start">
                    {formatDate(dates[0]!)}
                  </text>
                  <text x={VIEW_W - PAD_R} y={height - 3} className={s.axisDate} textAnchor="end">
                    {formatDate(dates[dates.length - 1]!)}
                  </text>
                </>
              )}
            </svg>
          );
        })}

        {active !== null && (
          <div ref={tooltipRef} className={s.tooltip} style={{ left: `${tooltipLeft}px` }}>
            <span className={s.tooltipDate}>{formatDate(active)}</span>
            {series.map((entry) => {
              const point = entry.points.find((p) => p.collectedOn === active);
              return (
                <span key={entry.analyteCode} className={s.tooltipRow}>
                  <i className={s.tooltipSwatch} data-series={entry.seriesIndex} aria-hidden="true" />
                  {/* The measurement recorded on that draw, or an explicit
                      "not measured" — never a value read off the line. */}
                  {entry.label}{" "}
                  {point ? `${point.value} ${entry.unit}` : t("trend.notMeasured", "ei mitattu")}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <Text as="p" size="xs" className={s.key}>
        <span className={s.keyItem}>
          <i className={s.keyMarker} aria-hidden="true" />
          {t("trend.keyLabProcessed", "Laboratorion tulos")}
        </span>
        <span className={s.keyItem}>
          <i className={s.keyMarkerOpen} aria-hidden="true" />
          {t("trend.keySelfReported", "Itse ilmoitettu")}
        </span>
      </Text>
    </div>
  );
}

/* Split a series wherever a draw carried no measurement for it, so the line
   breaks across the gap instead of drawing a straight segment through a date
   where nothing was measured. A lone point is still returned (as a
   one-element segment) — it renders as its marker with no line, which is the
   honest picture of a single isolated measurement.

   Generic over the point, and exported, so the combined chart next door
   breaks its lines by exactly the same rule rather than reimplementing it. */
export function segments<T extends { collectedOn: string }>(points: T[], dates: string[]): T[][] {
  const byDate = new Map(points.map((point) => [point.collectedOn, point]));
  const out: T[][] = [];
  let current: T[] = [];
  for (const date of dates) {
    const point = byDate.get(date);
    if (!point) {
      if (current.length > 0) {
        out.push(current);
        current = [];
      }
      continue;
    }
    current.push(point);
  }
  if (current.length > 0) {
    out.push(current);
  }
  return out;
}
